/**
 * ClerkEmailCodePanel — Email-code sign-in/sign-up using standard Clerk API.
 *
 * UX adapted from Magic Forest Sign In / Up:
 *   Step 1: "Sign In / Up" — email input → "Continue →"
 *   Step 2: 6 separate code cells → auto-submit → "Complete (n/6) →"
 *   Step 3: Success checkmark
 *
 * Renders as a field-container inside EditorPanel (no own header/wrapper).
 *
 * Uses @clerk/clerk-react v5 (standard API). Does NOT use the v6 Future API.
 * ONLY rendered when REACT_APP_USE_CLERK_AUTH=true.
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useSignIn, useSignUp, useClerk } from '@clerk/clerk-react';
import { useAuth } from '../../context/AuthContext';
import { getAuthHeaders } from '../../utils/authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const STEP_EMAIL = 'email';
const STEP_CODE = 'code';
const STEP_COMPLETE = 'complete';

export default function ClerkEmailCodePanel({ onBack, onSwitchToLegacy, onSuccess }) {
  const { signIn, isLoaded: signInLoaded } = useSignIn();
  const { signUp, isLoaded: signUpLoaded } = useSignUp();
  const { setActive } = useClerk();
  const { refreshAuth } = useAuth();

  const [step, setStep] = useState(STEP_EMAIL);
  const [email, setEmail] = useState('');
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSignInFlow, setIsSignInFlow] = useState(true);

  const inputRefs = useRef([]);

  const isLoaded = !!(signInLoaded && signUpLoaded);
  const filledCount = digits.filter(d => d !== '').length;
  const codeString = digits.join('');

  // ── Extract user-friendly error message ────────────────────────────
  const extractErrorMessage = (err, fallback) => {
    if (!err) return fallback;
    const first = err?.errors?.[0];
    if (first) return first.longMessage || first.message || fallback;
    if (err.message) return err.message;
    return fallback;
  };

  // ── Complete auth: setActive + refreshAuth + merge anonymous ──────
  const completeAuth = useCallback(async (createdSessionId) => {
    const anonymousToken = localStorage.getItem('mf_token');
    if (anonymousToken) {
      console.info('[ClerkEmailCodePanel] Captured anonymous token for merge');
    }

    try {
      await setActive({ session: createdSessionId });
      console.info('[ClerkEmailCodePanel] Clerk session activated');
    } catch (err) {
      console.error('[ClerkEmailCodePanel] setActive failed:', err);
      setError('Sign-in succeeded but session activation failed. Please try again.');
      return;
    }

    await new Promise(r => setTimeout(r, 600));

    if (refreshAuth) {
      const authResult = await refreshAuth();
      if (!authResult?.success) {
        console.error('[ClerkEmailCodePanel] refreshAuth failed:', authResult?.error);
        setError('Sign-in succeeded but local session failed. Please try again.');
        return;
      }
    }

    // Merge anonymous content
    if (anonymousToken) {
      try {
        console.info('[ClerkEmailCodePanel] Requesting anonymous content merge');
        const headers = await getAuthHeaders();
        const mergeRes = await fetch(`${BACKEND_URL}/api/auth/merge-anonymous`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ anonymous_token: anonymousToken }),
        });
        const mergeData = await mergeRes.json().catch(() => ({}));
        if (!mergeRes.ok) {
          throw new Error(mergeData?.detail || mergeData?.message || `Merge failed with HTTP ${mergeRes.status}`);
        }
        const status = mergeData?.merge_summary?.status;
        if (status === 'merged') {
          console.info('[ClerkEmailCodePanel] Content merged:', {
            carousels: mergeData.merge_summary.carousels_transferred,
            assets: mergeData.merge_summary.assets_transferred,
          });
        }
        localStorage.removeItem('mf_token');
        if (status === 'merged' && refreshAuth) await refreshAuth();
      } catch (mergeErr) {
        console.warn('[ClerkEmailCodePanel] Merge failed — token preserved:', mergeErr.message);
      }
    }

    setStep(STEP_COMPLETE);
    onSuccess?.();
  }, [setActive, refreshAuth, onSuccess]);

  // ── Step 1: Send email code ────────────────────────────────────────
  const handleSendCode = useCallback(async (e) => {
    e.preventDefault();
    if (!isLoaded) return;
    setError(null);
    setIsSubmitting(true);
    const normalizedEmail = email.trim().toLowerCase();

    try {
      try {
        const signInAttempt = await signIn.create({ identifier: normalizedEmail });
        if (signInAttempt.status === 'complete') {
          await completeAuth(signInAttempt.createdSessionId);
          return;
        }
        if (signInAttempt.status === 'needs_first_factor') {
          const emailFactor = signInAttempt.supportedFirstFactors?.find(f => f.strategy === 'email_code');
          if (!emailFactor) { setError('Email verification is not available for this account.'); return; }
          await signInAttempt.prepareFirstFactor({ strategy: 'email_code', emailAddressId: emailFactor.emailAddressId });
          setIsSignInFlow(true);
          setStep(STEP_CODE);
          return;
        }
        if (signInAttempt.status?.startsWith('needs_')) {
          await signInAttempt.prepareFirstFactor({
            strategy: 'email_code',
            emailAddressId: signInAttempt.supportedFirstFactors?.find(f => f.strategy === 'email_code')?.emailAddressId,
          });
          setIsSignInFlow(true);
          setStep(STEP_CODE);
          return;
        }
      } catch (signInError) {
        if (signInError.errors?.[0]?.code === 'form_identifier_not_found') {
          const signUpAttempt = await signUp.create({ emailAddress: normalizedEmail });
          if (signUpAttempt.status === 'complete') { await completeAuth(signUpAttempt.createdSessionId); return; }
          if (signUpAttempt.status === 'missing_requirements') {
            await signUpAttempt.prepareEmailAddressVerification({ strategy: 'email_code' });
            setIsSignInFlow(false);
            setStep(STEP_CODE);
            return;
          }
        } else { throw signInError; }
      }
    } catch (err) {
      console.error('[ClerkEmailCodePanel] handleSendCode exception:', err);
      setError(extractErrorMessage(err, 'Failed to send verification code. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  }, [isLoaded, signIn, signUp, email, completeAuth]);

  // ── Step 2: Verify code ────────────────────────────────────────────
  const handleVerifyCode = useCallback(async (codeToVerify) => {
    if (!isLoaded) return;
    setError(null);
    setIsSubmitting(true);
    try {
      if (isSignInFlow) {
        const result = await signIn.attemptFirstFactor({ strategy: 'email_code', code: codeToVerify });
        if (result.status === 'complete') { await completeAuth(result.createdSessionId); }
        else { setError('Verification incomplete. Please try again.'); }
      } else {
        const result = await signUp.attemptEmailAddressVerification({ code: codeToVerify });
        if (result.status === 'complete') { await completeAuth(result.createdSessionId); }
        else { setError('Verification incomplete (status: ' + result.status + '). Please try again.'); }
      }
    } catch (err) {
      console.error('[ClerkEmailCodePanel] Verification error:', err);
      setError(extractErrorMessage(err, 'Verification failed. Please check your code and try again.'));
    } finally {
      setIsSubmitting(false);
    }
  }, [isLoaded, signIn, signUp, isSignInFlow, completeAuth]);

  // ── 6-cell code input handlers ─────────────────────────────────────
  const handleDigitChange = (index, value) => {
    // Accept only single digit
    const digit = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);

    // Auto-advance to next cell
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 filled
    const fullCode = newDigits.join('');
    if (fullCode.length === 6 && newDigits.every(d => d !== '')) {
      handleVerifyCode(fullCode);
    }
  };

  const handleDigitKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleDigitPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const newDigits = [...digits];
    for (let i = 0; i < 6; i++) {
      newDigits[i] = pasted[i] || '';
    }
    setDigits(newDigits);
    // Focus last filled or next empty
    const nextEmpty = newDigits.findIndex(d => d === '');
    inputRefs.current[nextEmpty >= 0 ? nextEmpty : 5]?.focus();
    // Auto-submit if complete
    if (pasted.length === 6) {
      handleVerifyCode(pasted);
    }
  };

  // Focus first cell when entering code step
  useEffect(() => {
    if (step === STEP_CODE) {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [step]);

  // ── Success ────────────────────────────────────────────────────────
  if (step === STEP_COMPLETE) {
    return (
      <div className="field-container">
        <div className="text-center py-4">
          <div className="w-12 h-12 mx-auto bg-emerald-500/20 rounded-full flex items-center justify-center mb-3">
            <svg className="w-6 h-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-white font-medium">Signed in successfully</p>
          <p className="text-white/50 text-sm mt-1">Loading your workspace…</p>
        </div>
      </div>
    );
  }

  // ── Step 1: Email ──────────────────────────────────────────────────
  if (step === STEP_EMAIL) {
    return (
      <div className="field-container">
        <h3 className="text-center text-base font-semibold text-white/90 mb-2">Sign In / Up</h3>
        <p className="text-center text-white/50 text-sm mb-6">Fast & secure access. No password needed.</p>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSendCode}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email address"
            className="w-full bg-neutral-800/60 border border-neutral-600/50 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 focus:outline-none transition-colors mb-5"
            autoFocus
            disabled={isSubmitting}
          />

          {/* Clerk CAPTCHA placeholder */}
          <div id="clerk-captcha" data-cl-theme="dark" data-cl-size="flexible" />

          <button
            type="submit"
            disabled={isSubmitting || !email.trim() || !isLoaded}
            className={`w-full py-3 rounded-full font-medium text-sm transition-all ${
              !isSubmitting && email.trim() && isLoaded
                ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                : 'bg-neutral-700 text-neutral-500 cursor-not-allowed'
            }`}
          >
            {isSubmitting ? 'Sending code…' : 'Continue →'}
          </button>

          {onSwitchToLegacy && (
            <div className="text-center mt-4">
              <button type="button" onClick={onSwitchToLegacy} className="text-white/40 hover:text-white/70 text-xs transition-colors">
                Have a password? Sign in with password
              </button>
            </div>
          )}

          <div className="text-center mt-4">
            <button type="button" onClick={onBack} className="text-white/40 hover:text-white/70 text-sm transition-colors">Cancel</button>
          </div>
        </form>
      </div>
    );
  }

  // ── Step 2: Code ───────────────────────────────────────────────────
  return (
    <div className="field-container">
      <p className="text-center text-white/60 text-sm mb-1">Enter the verification code sent to</p>
      <p className="text-center text-white font-semibold mb-5">{email}</p>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* 6-cell code input */}
      <div className="flex justify-center gap-2 mb-3">
        {digits.map((digit, i) => (
          <input
            key={i}
            ref={el => inputRefs.current[i] = el}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleDigitChange(i, e.target.value)}
            onKeyDown={(e) => handleDigitKeyDown(i, e)}
            onPaste={i === 0 ? handleDigitPaste : undefined}
            disabled={isSubmitting}
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            className={`w-10 h-12 text-center text-lg font-mono rounded-lg border transition-colors focus:outline-none ${
              digit
                ? 'bg-neutral-800/80 border-emerald-500/50 text-white'
                : 'bg-neutral-800/40 border-neutral-600/50 text-white/80'
            } focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/30`}
          />
        ))}
      </div>

      <p className="text-center text-white/40 text-xs mb-5">Enter the 6-digit code • Auto-submits when complete</p>

      <button
        type="button"
        onClick={() => { if (codeString.length === 6) handleVerifyCode(codeString); }}
        disabled={isSubmitting || filledCount < 6}
        className={`w-full py-3 rounded-full font-medium text-sm transition-all ${
          !isSubmitting && filledCount === 6
            ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
            : 'bg-neutral-700 text-neutral-500 cursor-not-allowed'
        }`}
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Verifying…
          </span>
        ) : `Complete (${filledCount}/6) →`}
      </button>

      <div className="text-center mt-4">
        <button
          type="button"
          onClick={() => { setStep(STEP_EMAIL); setDigits(['','','','','','']); setError(null); }}
          className="text-white/40 hover:text-white/70 text-sm transition-colors"
        >
          ← Back to email
        </button>
      </div>
    </div>
  );
}
